/**
 *
 * Search Service Sagas
 *
 */

import { take, call, put, select } from 'redux-saga/effects';
import moment from 'moment-timezone';
import { DEFAULT_DATE_FORMAT_TIMEZONE, DEFAULT_DATE_TIMEZONE, TRANSFER_TYPES } from 'common';
import isObject from 'lodash/isObject';
import isFunction from 'lodash/isFunction';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import get from 'lodash/get';
import { setFilteredResultsCount } from 'containers/Search/actions';
import { notAsked, loading, success, failure } from './actions';
import { searchRequest } from './api';
import { SEARCH_REQUEST, FED_REFERENCE_NUMBER_TYPES } from './constants';
import { selectSearchShowAllFields } from './selectors';

const formatIdType = (id) => (id ? ` (${id})` : '');

const getBankIdentifier = (method, bankAccount, recipientBank) => {
  if (method === TRANSFER_TYPES.wire) {
    const type = get(recipientBank, 'type', '');
    const value = get(recipientBank, 'id', '');
    return {
      type: get(recipientBank, 'type', ''),
      value: `${value}${formatIdType(type)}`,
    };
  }
  return {
    type: get(bankAccount, 'identifier.type', ''),
    value: get(bankAccount, 'identifier.value', ''),
  };
};

export function normalizeResponse(results, hasFullExternalPermissions = false) {
  if (!isPlainObject(results) || !isArray(results.evaluatedTransfers)) {
    return [];
  }
  const { nextPageToken, previousPageToken, totalSize } = results;
  const rows = results.evaluatedTransfers.map((row) => {
    const {
      programName = '',
      amount,
      batchId,
      ctsId,
      destinationAccount,
      destinationCountry,
      direction,
      bankConfirmation,
      initiationTime,
      instructionSource,
      method,
      sourceCountry,
      state,
      transferId,
      apexAccountNumber,
      achRelationshipId,
      bankAccount,
      correspondent,
      requestedAmount,
      isFullDisbursement,
      recipientBankIdentifier,
      fedWireReferenceNumber,
      externalId,
      houseBankAccountNumber,
      journalType,
    } = row;

    /*
     * Merge bankAccountIdentifier and fedWireReferenceNumber
     * into single column, "Fed Red #."
     * bankAccountIdentifier is for wire withdrawals.
     * fedWireReferenceNumber in for incoming wires.
     */
    const bankAccountIdentifier = getBankIdentifier(method, bankAccount, recipientBankIdentifier);
    const fedReferenceNumber = get(bankConfirmation, 'id', '');
    const fedReferenceType = FED_REFERENCE_NUMBER_TYPES[get(bankConfirmation, 'type', '')];
    const fedWireRefAndBankAccountIdentifier = fedWireReferenceNumber
      ? row.fedWireReferenceNumber
      : `${fedReferenceNumber}${formatIdType(fedReferenceType)}`;

    return {
      programName: hasFullExternalPermissions && programName,
      amount,
      bankAccountType: bankAccount ? bankAccount.type : '',
      bankAccountNumber: bankAccount ? bankAccount.number : '',
      batchId,
      achRelationshipId,
      bankAccountIdentifierType: get(bankAccountIdentifier, 'type', ''),
      bankAccountIdentifierValue: get(bankAccountIdentifier, 'value', ''),
      apexAccountNumber,
      correspondentCode: hasFullExternalPermissions && get(correspondent, 'code', ''),
      // FEECM-16278
      // correspondentDeposit: hasFullExternalPermissions && get(correspondent, 'deposit', 0),
      correspondentName: hasFullExternalPermissions && get(correspondent, 'name', ''),
      date: moment.tz(initiationTime, moment.ISO_8601, DEFAULT_DATE_TIMEZONE).format(DEFAULT_DATE_FORMAT_TIMEZONE),
      direction,
      id: transferId,
      initiationTime,
      instructionSource,
      method,
      requestedAmount,
      state,
      isFullDisbursement: direction !== 'DEPOSIT' ? isFullDisbursement : null,
      ctsId,
      destinationAccount,
      destinationCountry,
      sourceCountry,
      fedWireReferenceNumber: fedWireRefAndBankAccountIdentifier,
      externalId,
      houseBankAccountNumber,
      journalType,
    };
  });
  return {
    rows,
    nextPageToken,
    previousPageToken,
    totalSize,
  };
}

export function* requestFlow(params) {
  // Set the status to LOADING and store the params
  yield put(loading(params));

  try {
    // Make the request
    yield put(setFilteredResultsCount(0));
    const results = yield call(searchRequest, params);
    const fullPermissions = yield select(selectSearchShowAllFields);
    const normalizedResults = normalizeResponse(results, fullPermissions);

    yield put(success(normalizedResults));
    yield put(setFilteredResultsCount(normalizedResults.rows.length));
  } catch (err) {
    let errorBody = err;
    if (isObject(err.response)) {
      if (isFunction(err.response.json)) {
        errorBody = yield err.response.json();
      }
      errorBody.status = err.response.status;
    } else {
      errorBody = errorBody.toString();
    }
    // The .json() method turns the response body into a promise.
    // Set status to FAILURE and pass along the error
    yield put(failure(errorBody));
  }
}

export function* searchServiceFlow() {
  // Set the initial status
  yield put(notAsked());

  while (true) {
    // Watch for SEARCH_REQUEST to be dispatched
    const { params } = yield take(SEARCH_REQUEST);
    // Make the request
    yield call(requestFlow, params);
  }
}

export default [searchServiceFlow()];



// WEBPACK FOOTER //
// ./src/services/search/sagas.js